import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  periodsQueryOptions,
  useExactIncident,
  type OperationalPeriod,
} from 'components/IAP/hooks/incidentHooks';
import {
  useWorkAssignmentsForIncident,
  type WorkAssignment,
} from 'components/IAP/hooks/workAssignmentHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import { getTimezoneLabel } from 'constants/timezones';

type IncidentDetailsSummaryProps = {
  incidentId: string;
  resourceSetId?: string;
  datetimeOrdered?: string;
  datetimeOrderedOffset?: number;
  eta?: string;
  etaTimezoneOffset?: number;
  notes?: string;
};

const fmt = (date?: string) =>
  date ? dayjs(date).format('MM/DD HH:mm') : null;

const formatPeriodRange = (
  periodNumber?: number,
  start?: string,
  end?: string
): string => {
  const prefix = periodNumber != null ? `Period ${periodNumber}: ` : '';
  const s = fmt(start);
  const e = fmt(end);
  if (!s && !e) return `${prefix}${NULL_VALUE}`;
  if (s && !e) return `${prefix}${s}`;
  return `${prefix}${s ?? NULL_VALUE} – ${e}`;
};

const getAssignment = (
  workAssignments: WorkAssignment[] | undefined,
  resourceSetId: string | undefined,
  periodNumber?: number
): string => {
  if (!workAssignments || !resourceSetId || periodNumber == null)
    return NULL_VALUE;
  return (
    workAssignments
      .filter(
        (wa) =>
          !wa.archived &&
          wa.resource_sets?.includes(resourceSetId) &&
          wa.operational_period_number === periodNumber
      )
      .map((wa) => wa.work_assignment)
      .filter(Boolean)
      .join(', ') || NULL_VALUE
  );
};

const getCurrentPeriod = (
  periods: OperationalPeriod[] | undefined,
  currentPeriodId?: string
): OperationalPeriod | undefined => {
  if (!periods?.length) return undefined;
  return (
    periods.find((p) => p.id === currentPeriodId) ??
    periods.reduce((a, b) =>
      (a.period_number ?? 0) >= (b.period_number ?? 0) ? a : b
    )
  );
};

const IncidentDetailsSummary: React.FC<IncidentDetailsSummaryProps> = ({
  incidentId,
  resourceSetId,
  datetimeOrdered,
  datetimeOrderedOffset,
  eta,
  etaTimezoneOffset,
  notes,
}) => {
  const { data: incident } = useExactIncident(incidentId);
  const { data: periods } = useQuery(
    periodsQueryOptions({ incidentId, enabled: !!incidentId })
  );
  const { data: workAssignments } = useWorkAssignmentsForIncident(
    incidentId,
    !!resourceSetId
  );

  if (!incident) return null;

  const current = getCurrentPeriod(periods, incident.current_period);
  const currentNum = current?.period_number;
  const currentEnd =
    current?.anticipated_end_date_time ?? current?.end_date_time;
  const next = periods?.find((p) => p.period_number === (currentNum ?? 0) + 1);
  const nextNum = (currentNum ?? 0) + 1;

  return (
    <div className="d-flex flex-column gap-2">
      <div className="fw-bold">{incident.name} Incident Details</div>
      <div className="d-flex">
        <div className="col-6">
          <div className="fw-semibold small">Datetime Ordered</div>
          <div>
            {datetimeOrdered
              ? `${dayjs(datetimeOrdered).format('MM/DD/YYYY HH:mm')} ${getTimezoneLabel(datetimeOrderedOffset ?? 0)}`
              : NULL_VALUE}
          </div>
        </div>
        <div className="col-6">
          <div className="fw-semibold small">Estimated Time of Arrival</div>
          <div>
            {eta
              ? `${dayjs(eta).format('MM/DD/YYYY HH:mm')} ${getTimezoneLabel(etaTimezoneOffset ?? 0)}`
              : NULL_VALUE}
          </div>
        </div>
      </div>
      <div className="d-flex">
        <div className="col-6">
          <div className="fw-semibold small">Current Op Period</div>
          <div>
            {formatPeriodRange(
              currentNum,
              current?.start_date_time,
              currentEnd
            )}
          </div>
        </div>
        <div className="col-6">
          <div className="fw-semibold small">Next Op Period</div>
          <div>
            {next
              ? formatPeriodRange(
                  next.period_number,
                  currentEnd,
                  next.anticipated_end_date_time ?? next.end_date_time
                )
              : formatPeriodRange(nextNum, currentEnd)}
          </div>
        </div>
      </div>
      <div className="d-flex">
        <div className="col-6">
          <div className="fw-semibold small">Current Op Period Assignment</div>
          <div>{getAssignment(workAssignments, resourceSetId, currentNum)}</div>
        </div>
        <div className="col-6">
          <div className="fw-semibold small">Next Op Period Assignment</div>
          <div>
            {getAssignment(workAssignments, resourceSetId, next?.period_number)}
          </div>
        </div>
      </div>
      <div className="d-flex">
        <div className="col-12">
          <div className="fw-semibold small">Notes</div>
          <div>{notes || NULL_VALUE}</div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailsSummary;
